import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { React, useState, useEffect, useContext, useCallback } from 'react';
import * as menu from "../../assets/menus/menu.js"
import * as api from "../../api/apiConnector.js";
import UserContext from '../../components/User_Context.js';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import Undo_Redo from "../../components/ui/undo_redo.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';

const Form_Custom_Vocs_Screen = () => {
    // Original data from server
    const [originalVocabularies, setOriginalVocabularies] = useState([]);
    const [originalVocabularyValues, setOriginalVocabularyValues] = useState({});
    
    // Local working data
    const [vocabularies, setVocabularies] = useState([]);
    const [vocabularyValues, setVocabularyValues] = useState({});
    const [pendingChanges, setPendingChanges] = useState({
        vocabularies: {
            added: [],
            modified: [],
            deleted: []
        },
        values: {
            added: [],
            modified: [],
            deleted: []
        }
    });
    
    const [selectedVocabulary, setSelectedVocabulary] = useState(null);
    const [newVocabularyName, setNewVocabularyName] = useState('');
    const [newValueName, setNewValueName] = useState('');
    const [editingVocabulary, setEditingVocabulary] = useState(null);
    const [editingValue, setEditingValue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    
    // Pagination and search state
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total_records: 0,
        total_pages: 0,
        has_next_page: false,
        has_previous_page: false
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation();
    let undo_redo = null;

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    // Generate temporary ID for new items
    const generateTempId = () => 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    // Debounce function to avoid too many API calls
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    const setBreadCrumb = (params) => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin/form_custom_vocs",
            name: t('admin', { ns: 'labels' })
        },
        {
            link: "/admin/form_custom_vocs",
            name: t('formCustomVocs', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    };

    // Load vocabularies from server with pagination
    const loadVocabularies = async (page = 1, search = '', loadAllValues = false) => {
        try {
            setLoading(true);
            
            const result = await api.sendMessage("admin/form_custom_vocs/get_all", {
                user: currentUser(),
                body: {
                    paginate: true,    // Enable pagination
                    page: page,        // Requested page
                    limit: 20,         // Results per page
                    search: search     // Search term
                }
            });

            if (result.success) {
                const vocabs = result.data?.vocabularies || result.vocabularies || [];
                const paginationData = result.data?.pagination || {};
                
                // Update state with paginated results
                setOriginalVocabularies(vocabs);
                setVocabularies(vocabs);
                setPagination(paginationData);
                
                // Load values only if requested (for performance)
                if (loadAllValues) {
                    await loadVocabularyValues(vocabs);
                }
            } else {
                setError("Failed to load vocabularies");
            }
        } catch (err) {
            console.error("Error loading vocabularies:", err);
            setError("Error loading vocabularies");
        } finally {
            setLoading(false);
        }
    };

    // Load vocabulary values only when necessary (lazy loading)
    const loadVocabularyValues = async (vocabularies) => {
        try {
            const allValues = {};
            
            // Use Promise.all to load in parallel (faster)
            const valuePromises = vocabularies.map(async (vocab) => {
                const valuesResult = await api.sendMessage("admin/form_custom_vocs/get_values", {
                    user: currentUser(),
                    body: { vocId: vocab.Id }
                });
                
                if (valuesResult.success) {
                    return { vocabId: vocab.Id, values: valuesResult.values || [] };
                }
                return { vocabId: vocab.Id, values: [] };
            });
            
            const results = await Promise.all(valuePromises);
            
            // Build object with all values
            results.forEach(({ vocabId, values }) => {
                allValues[vocabId] = values;
            });
            
            setOriginalVocabularyValues(prevValues => ({ ...prevValues, ...allValues }));
            setVocabularyValues(prevValues => ({ ...prevValues, ...allValues }));
            
        } catch (err) {
            console.error("Error loading vocabulary values:", err);
        }
    };

    // Load values only when user selects a vocabulary
    const loadValuesForVocabulary = async (vocabId) => {
        if (vocabularyValues[vocabId]) {
            return; // Already loaded
        }
        
        try {
            const valuesResult = await api.sendMessage("admin/form_custom_vocs/get_values", {
                user: currentUser(),
                body: { vocId: vocabId }
            });
            
            if (valuesResult.success) {
                setVocabularyValues(prev => ({
                    ...prev,
                    [vocabId]: valuesResult.values || []
                }));
                setOriginalVocabularyValues(prev => ({
                    ...prev,
                    [vocabId]: valuesResult.values || []
                }));
            }
        } catch (err) {
            console.error(`Error loading values for vocabulary ${vocabId}:`, err);
        }
    };

    // Add vocabulary locally
    const addVocabularyLocally = () => {
        if (!newVocabularyName.trim()) {
            try {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: t('pleaseEnterVocabularyName', { ns: 'labels' }),
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
            return;
        }

        const tempId = generateTempId();
        const newVocab = {
            Id: tempId,
            Name: newVocabularyName.trim(),
            isNew: true
        };

        setVocabularies(prev => [...prev, newVocab]);
        setVocabularyValues(prev => ({ ...prev, [tempId]: [] }));
        
        setPendingChanges(prev => ({
            ...prev,
            vocabularies: {
                ...prev.vocabularies,
                added: [...prev.vocabularies.added, newVocab]
            }
        }));

        setNewVocabularyName('');
        setHasChanges(true);
    };

    // Start editing vocabulary
    const startEditingVocabulary = (vocabulary) => {
        setEditingVocabulary({
            ...vocabulary,
            originalName: vocabulary.Name
        });
    };

    // Save vocabulary edit
    const saveVocabularyEdit = (newName) => {
        if (!newName.trim()) {
            try {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: t('pleaseEnterVocabularyName', { ns: 'labels' }),
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
            return;
        }

        const updatedVocabularies = vocabularies.map(vocab => {
            if (vocab.Id === editingVocabulary.Id) {
                return { ...vocab, Name: newName.trim() };
            }
            return vocab;
        });

        setVocabularies(updatedVocabularies);

        // Update pending changes
        if (editingVocabulary.isNew) {
            // Update in added list
            setPendingChanges(prev => ({
                ...prev,
                vocabularies: {
                    ...prev.vocabularies,
                    added: prev.vocabularies.added.map(vocab => 
                        vocab.Id === editingVocabulary.Id ? { ...vocab, Name: newName.trim() } : vocab
                    )
                }
            }));
        } else {
            // Add to modified list if not already there
            setPendingChanges(prev => {
                const existingModified = prev.vocabularies.modified.find(v => v.Id === editingVocabulary.Id);
                if (existingModified) {
                    return {
                        ...prev,
                        vocabularies: {
                            ...prev.vocabularies,
                            modified: prev.vocabularies.modified.map(vocab => 
                                vocab.Id === editingVocabulary.Id ? { ...vocab, Name: newName.trim() } : vocab
                            )
                        }
                    };
                } else {
                    return {
                        ...prev,
                        vocabularies: {
                            ...prev.vocabularies,
                            modified: [...prev.vocabularies.modified, { ...editingVocabulary, Name: newName.trim() }]
                        }
                    };
                }
            });
        }

        setEditingVocabulary(null);
        setHasChanges(true);
    };

    // Cancel vocabulary edit
    const cancelVocabularyEdit = () => {
        setEditingVocabulary(null);
    };

    // Start editing value
    const startEditingValue = (value) => {
        setEditingValue({
            ...value,
            originalValue: value.Value
        });
    };

    // Save value edit
    const saveValueEdit = (newValue) => {
        if (!newValue.trim()) {
            try {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: t('pleaseEnterValueName', { ns: 'labels' }),
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
            return;
        }

        const updatedValues = vocabularyValues[selectedVocabulary.Id].map(val => {
            if (val.Id === editingValue.Id) {
                return { ...val, Value: newValue.trim() };
            }
            return val;
        });

        setVocabularyValues(prev => ({
            ...prev,
            [selectedVocabulary.Id]: updatedValues
        }));

        // Update pending changes
        if (editingValue.isNew) {
            // Update in added list
            setPendingChanges(prev => ({
                ...prev,
                values: {
                    ...prev.values,
                    added: prev.values.added.map(val => 
                        val.Id === editingValue.Id ? { ...val, Value: newValue.trim() } : val
                    )
                }
            }));
        } else {
            // Add to modified list if not already there
            setPendingChanges(prev => {
                const existingModified = prev.values.modified.find(v => v.Id === editingValue.Id);
                if (existingModified) {
                    return {
                        ...prev,
                        values: {
                            ...prev.values,
                            modified: prev.values.modified.map(val => 
                                val.Id === editingValue.Id ? { ...val, Value: newValue.trim() } : val
                            )
                        }
                    };
                } else {
                    return {
                        ...prev,
                        values: {
                            ...prev.values,
                            modified: [...prev.values.modified, { ...editingValue, Value: newValue.trim() }]
                        }
                    };
                }
            });
        }

        setEditingValue(null);
        setHasChanges(true);
    };

    // Cancel value edit
    const cancelValueEdit = () => {
        setEditingValue(null);
    };

    // Delete vocabulary locally
    const deleteVocabularyLocally = (vocId) => {
        const vocabulary = vocabularies.find(v => v.Id === vocId);
        if (!vocabulary) return;

        const confirmDelete = async () => {
            try {
                const result = await withReactContent(Swal).fire({
                    title: t('confirm', { ns: 'labels' }),
                    icon: "warning",
                    text: t('confirmDeleteVocabulary', { ns: 'labels' }),
                    showCancelButton: true,
                    confirmButtonText: t('delete', { ns: 'labels' }),
                    cancelButtonText: t('cancel', { ns: 'labels' }),
                });

                if (result.isConfirmed) {
                    // Remove from local state
                    setVocabularies(prev => prev.filter(v => v.Id !== vocId));
                    setVocabularyValues(prev => {
                        const updated = { ...prev };
                        delete updated[vocId];
                        return updated;
                    });

                    // Update pending changes
                    if (vocabulary.isNew) {
                        // Remove from added list
                        setPendingChanges(prev => ({
                            ...prev,
                            vocabularies: {
                                ...prev.vocabularies,
                                added: prev.vocabularies.added.filter(v => v.Id !== vocId)
                            }
                        }));
                    } else {
                        // Add to deleted list
                        setPendingChanges(prev => ({
                            ...prev,
                            vocabularies: {
                                ...prev.vocabularies,
                                deleted: [...prev.vocabularies.deleted, vocabulary]
                            }
                        }));
                    }

                    // Clear selection if this was selected
                    if (selectedVocabulary?.Id === vocId) {
                        setSelectedVocabulary(null);
                    }

                    setHasChanges(true);
                }
            } catch (error) {
                console.error('Error in delete confirmation:', error);
            }
        };

        confirmDelete();
    };

    // Add value locally
    const addValueLocally = () => {
        if (!newValueName.trim() || !selectedVocabulary) {
            try {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: t('pleaseEnterValueName', { ns: 'labels' }),
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
            return;
        }

        const tempId = generateTempId();
        const newValue = {
            Id: tempId,
            Value: newValueName.trim(),
            vocId: selectedVocabulary.Id,
            isNew: true
        };

        setVocabularyValues(prev => ({
            ...prev,
            [selectedVocabulary.Id]: [...(prev[selectedVocabulary.Id] || []), newValue]
        }));

        setPendingChanges(prev => ({
            ...prev,
            values: {
                ...prev.values,
                added: [...prev.values.added, newValue]
            }
        }));

        setNewValueName('');
        setHasChanges(true);
    };

    // Delete value locally
    const deleteValueLocally = (valueId) => {
        const value = vocabularyValues[selectedVocabulary.Id]?.find(v => v.Id === valueId);
        if (!value) return;

        const confirmDelete = async () => {
            try {
                const result = await withReactContent(Swal).fire({
                    title: t('confirm', { ns: 'labels' }),
                    icon: "warning",
                    text: t('confirmDeleteValue', { ns: 'labels' }),
                    showCancelButton: true,
                    confirmButtonText: t('delete', { ns: 'labels' }),
                    cancelButtonText: t('cancel', { ns: 'labels' }),
                });

                if (result.isConfirmed) {
                    // Remove from local state
                    setVocabularyValues(prev => ({
                        ...prev,
                        [selectedVocabulary.Id]: prev[selectedVocabulary.Id].filter(v => v.Id !== valueId)
                    }));

                    // Update pending changes
                    if (value.isNew) {
                        // Remove from added list
                        setPendingChanges(prev => ({
                            ...prev,
                            values: {
                                ...prev.values,
                                added: prev.values.added.filter(v => v.Id !== valueId)
                            }
                        }));
                    } else {
                        // Add to deleted list
                        setPendingChanges(prev => ({
                            ...prev,
                            values: {
                                ...prev.values,
                                deleted: [...prev.values.deleted, value]
                            }
                        }));
                    }

                    setHasChanges(true);
                }
            } catch (error) {
                console.error('Error in delete confirmation:', error);
            }
        };

        confirmDelete();
    };

    // Save all changes
    const saveAllChanges = async () => {
        // Check if there are any changes to save
        if (!hasChanges) {
            try {
                withReactContent(Swal).fire({
                    title: 'INFO',
                    icon: 'info',
                    text: t('nothingToSave', { ns: 'labels' }),
                    showConfirmButton: true,
                    timer: 1500,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
            return;
        }

        setSaving(true);
        try {
            // Handle vocabulary deletions first
            for (const vocab of pendingChanges.vocabularies.deleted) {
                const result = await api.sendMessage("admin/form_custom_vocs/delete_voc", {
                    user: currentUser(),
                    body: { vocId: vocab.Id }
                });
                if (!result.success) {
                    throw new Error(`Failed to delete vocabulary: ${vocab.Name}`);
                }
            }

            // Handle value deletions
            for (const value of pendingChanges.values.deleted) {
                const result = await api.sendMessage("admin/form_custom_vocs/delete_value", {
                    user: currentUser(),
                    body: { valueId: value.Id }
                });
                if (!result.success) {
                    throw new Error(`Failed to delete value: ${value.Value}`);
                }
            }

            // Handle vocabulary updates
            for (const vocab of pendingChanges.vocabularies.modified) {
                const result = await api.sendMessage("admin/form_custom_vocs/update", {
                    user: currentUser(),
                    body: { vocId: vocab.Id, name: vocab.Name }
                });
                if (!result.success) {
                    throw new Error(`Failed to update vocabulary: ${vocab.Name}`);
                }
            }

            // Handle new vocabularies with their values
            for (const vocab of pendingChanges.vocabularies.added) {
                // Get values for this vocabulary
                const vocValues = vocabularyValues[vocab.Id] || [];
                const tempValues = vocValues
                    .filter(val => val.isNew)
                    .map(val => ({
                        value: val.Value,
                        valueId: val.Id
                    }));

                const result = await api.sendMessage("admin/form_custom_vocs/create", {
                    user: currentUser(),
                    body: { 
                        name: vocab.Name,
                        tempId: vocab.Id,
                        tempValues: tempValues
                    }
                });
                if (!result.success) {
                    throw new Error(`Failed to create vocabulary: ${vocab.Name}`);
                }
            }

            // Handle value updates
            for (const value of pendingChanges.values.modified) {
                const result = await api.sendMessage("admin/form_custom_vocs/update_value", {
                    user: currentUser(),
                    body: { valueId: value.Id, value: value.Value }
                });
                if (!result.success) {
                    throw new Error(`Failed to update value: ${value.Value}`);
                }
            }

            // Handle new values for existing vocabularies
            const newValuesForExisting = pendingChanges.values.added.filter(value => {
                // Check if the vocId is not a temporary ID (not in the added vocabularies)
                return !pendingChanges.vocabularies.added.some(vocab => vocab.Id === value.vocId);
            });

            for (const value of newValuesForExisting) {
                const result = await api.sendMessage("admin/form_custom_vocs/create_value", {
                    user: currentUser(),
                    body: { vocId: value.vocId, value: value.Value }
                });
                if (!result.success) {
                    throw new Error(`Failed to create value: ${value.Value}`);
                }
            }

            // Reset pending changes
            setPendingChanges({
                vocabularies: {
                    added: [],
                    modified: [],
                    deleted: []
                },
                values: {
                    added: [],
                    modified: [],
                    deleted: []
                }
            });

            setHasChanges(false);
            
            // Reload data keeping current page and search
            await loadVocabularies(currentPage, searchTerm, false);

            try {
                withReactContent(Swal).fire({
                    title: t('success', { ns: 'labels' }),
                    icon: "success",
                    text: t('changesSaved', { ns: 'labels' }),
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }

        } catch (err) {
            console.error("Error saving changes:", err);
            try {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: err.message || "Error saving changes",
                    showConfirmButton: true,
                });
            } catch (error) {
                console.error('Error showing alert:', error);
            }
        } finally {
            setSaving(false);
        }
    };

    // Handle vocabulary selection with lazy loading
    const handleVocabularySelect = async (vocabulary) => {
        setSelectedVocabulary(vocabulary);
        await loadValuesForVocabulary(vocabulary.Id);
    };

    // Debounce search to avoid too many API calls
    const debounceSearch = useCallback(
        debounce((term) => {
            setCurrentPage(1); // Reset to first page
            loadVocabularies(1, term, false);
        }, 300),
        []
    );

    useEffect(() => {
        let isMounted = true;
        
        const initializeComponent = async () => {
            try {
                if (isMounted) {
                    menu.write({ user: currentUser() });
                    await loadVocabularies(1, '', false); // Load first page without values
                    
                    // Set breadcrumb after DOM is ready
                    if (isMounted) {
                        setTimeout(() => {
                            if (isMounted) {
                                setBreadCrumb();
                            }
                        }, 100);
                    }
                }
            } catch (error) {
                console.error('Error initializing component:', error);
                if (isMounted) {
                    setError('Failed to initialize component');
                }
            }
        };
        
        initializeComponent();
        
        return () => {
            isMounted = false;
        };
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
            
            {/* HEADER + TOOLBAR (sticky) */}
            <header className="bg-white shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    {/* breadcrumb navigation */}
                    <ol className="breadcrumb mb-0"></ol>
                    
                    {/* action buttons */}
                    <div className="d-flex gap-3">
                        <a
                            className="text-primary fs-13"
                            onClick={(e) => {
                                saveAllChanges();
                            }}
                            role="button"
                            title={t('saveThisData', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-floppy-disk fa-2x"></i>
                        </a>
                    </div>
                </div>
            </header>

            {/* SCROLLABLE CONTENT AREA */}
            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid p-2 p-md-3">


                    <div className="row g-3" style={{ height: '100%' }}>
                        {/* Left Panel - Vocabularies */}
                        <div className="col-12 col-lg-7">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">{t('vocabularies', { ns: 'labels' })}</h5>
                                </div>
                                <div className="card-body">
                                    {/* Search Bar */}
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{ fontSize: '16px' }}
                                            placeholder={t('searchVocabulary', { ns: 'labels' }) || 'Cerca vocabolario...'}
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                debounceSearch(e.target.value);
                                            }}
                                        />
                                    </div>
                                    {/* Add new vocabulary */}
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{ fontSize: '16px' }} // Prevents zoom on iOS
                                                placeholder={t('vocabularyName', { ns: 'labels' })}
                                                value={newVocabularyName}
                                                onChange={(e) => setNewVocabularyName(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && addVocabularyLocally()}
                                            />
                                            <i 
                                                style={{ cursor: 'pointer', color: '#6c757d' }} 
                                                data-bs-tooltip="tooltip" 
                                                data-bs-placement='top' 
                                                title={t('add', { ns: 'labels' })} 
                                                onClick={addVocabularyLocally}
                                                className="fa-regular fa-square-plus fa-2xl hover-icon-add" 
                                            />
                                        </div>
                                    </div>

                                    {/* Vocabularies list */}
                                    <div className="table-responsive" style={{ position: 'relative' }}>
                                        {loading && (
                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        )}
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '70%' }}>{t('name', { ns: 'labels' })}</th>
                                                    <th style={{ width: '30%' }}>{t('actions', { ns: 'labels' })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {vocabularies.length === 0 && !loading ? (
                                                    <tr>
                                                        <td colSpan="2" className="text-center text-muted py-4">
                                                            {searchTerm ? 
                                                                (t('noVocabulariesFound', { ns: 'labels' }) || 'Nessun vocabolario trovato per la ricerca') :
                                                                (t('noVocabularies', { ns: 'labels' }) || 'Nessun vocabolario presente')
                                                            }
                                                        </td>
                                                    </tr>
                                                ) : null}
                                                {vocabularies.map((vocabulary) => (
                                                    <tr
                                                        key={vocabulary.Id}
                                                        className={`${selectedVocabulary?.Id === vocabulary.Id ? 'table-active' : ''} ${vocabulary.isNew ? 'table-success' : ''}`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleVocabularySelect(vocabulary)}
                                                    >
                                                        <td style={{ wordBreak: 'break-word', maxWidth: '0' }}>
                                                            {editingVocabulary?.Id === vocabulary.Id ? (
                                                                <div className="input-group input-group-sm">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        style={{ fontSize: '16px' }} // Prevents zoom on iOS
                                                                        value={editingVocabulary.Name}
                                                                        onChange={(e) => setEditingVocabulary({...editingVocabulary, Name: e.target.value})}
                                                                        onKeyPress={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                saveVocabularyEdit(editingVocabulary.Name);
                                                                            } else if (e.key === 'Escape') {
                                                                                cancelVocabularyEdit();
                                                                            }
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        autoFocus
                                                                    />
                                                                    <button
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            saveVocabularyEdit(editingVocabulary.Name);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-check"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-secondary"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            cancelVocabularyEdit();
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-times"></i>
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={vocabulary.Name}>
                                                                    {vocabulary.Name}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {editingVocabulary?.Id !== vocabulary.Id && (
                                                                <div>
                                                                    <i 
                                                                        style={{ cursor: 'pointer', color: '#17a2b8' }} 
                                                                        data-bs-tooltip="tooltip" 
                                                                        data-bs-placement='top' 
                                                                        title={t('edit', { ns: 'labels' })} 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            startEditingVocabulary(vocabulary);
                                                                        }}
                                                                        className="fa-regular fa-pen-to-square me-4 fa-2xl hover-icon-edit" 
                                                                    />
                                                                    <i 
                                                                        style={{ cursor: 'pointer', color: '#dc3545' }} 
                                                                        data-bs-tooltip="tooltip" 
                                                                        data-bs-placement='top' 
                                                                        title={t('delete', { ns: 'labels' })} 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            deleteVocabularyLocally(vocabulary.Id);
                                                                        }}
                                                                        className="fa fa-trash fa-2xl hover-icon-trash" 
                                                                    />
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    {pagination.total_pages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={!pagination.has_previous_page || loading}
                                                    onClick={() => {
                                                        const newPage = currentPage - 1;
                                                        setCurrentPage(newPage);
                                                        loadVocabularies(newPage, searchTerm, false);
                                                    }}
                                                >
                                                    <i className="fa fa-chevron-left me-1"></i>
                                                    {t('previous', { ns: 'labels' }) || 'Precedente'}
                                                </button>
                                                
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={!pagination.has_next_page || loading}
                                                    onClick={() => {
                                                        const newPage = currentPage + 1;
                                                        setCurrentPage(newPage);
                                                        loadVocabularies(newPage, searchTerm, false);
                                                    }}
                                                >
                                                    {t('next', { ns: 'labels' }) || 'Successiva'}
                                                    <i className="fa fa-chevron-right ms-1"></i>
                                                </button>
                                            </div>
                                            
                                            <small className="text-muted">
                                                {t('page', { ns: 'labels' }) || 'Pagina'} {pagination.current_page || currentPage} {t('of', { ns: 'labels' }) || 'di'} {pagination.total_pages}
                                                <br />
                                                ({pagination.total_records} {t('totalRecords', { ns: 'labels' }) || 'totali'})
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Panel - Vocabulary Values */}
                        <div className="col-12 col-lg-5">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">
                                        {t('values', { ns: 'labels' })} {selectedVocabulary ? `- ${selectedVocabulary.Name}` : ''}
                                        {selectedVocabulary && !vocabularyValues[selectedVocabulary.Id] && (
                                            <div className="spinner-border spinner-border-sm ms-2" role="status">
                                                <span className="visually-hidden">Loading values...</span>
                                            </div>
                                        )}
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {selectedVocabulary ? (
                                        <>
                                            {/* Add new value */}
                                            <div className="mb-3">
                                                                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ fontSize: '16px' }} // Prevents zoom on iOS
                                                    placeholder={t('valueName', { ns: 'labels' })}
                                                    value={newValueName}
                                                    onChange={(e) => setNewValueName(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && addValueLocally()}
                                                />
                                                <i 
                                                    style={{ cursor: 'pointer', color: '#6c757d' }} 
                                                    data-bs-tooltip="tooltip" 
                                                    data-bs-placement='top' 
                                                    title={t('add', { ns: 'labels' })} 
                                                    onClick={addValueLocally}
                                                    className="fa-regular fa-square-plus fa-2xl hover-icon-add" 
                                                />
                                            </div>
                                            </div>

                                            {/* Values list */}
                                            <div className="table-responsive">
                                                <table className="table table-hover" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '70%' }}>{t('value', { ns: 'labels' })}</th>
                                                            <th style={{ width: '30%' }}>{t('actions', { ns: 'labels' })}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(vocabularyValues[selectedVocabulary.Id] || []).map((value) => (
                                                            <tr key={value.Id} className={value.isNew ? 'table-success' : ''}>
                                                                <td style={{ wordBreak: 'break-word', maxWidth: '0' }}>
                                                                    {editingValue?.Id === value.Id ? (
                                                                        <div className="input-group input-group-sm">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                style={{ fontSize: '16px' }} // Prevents zoom on iOS
                                                                                value={editingValue.Value}
                                                                                onChange={(e) => setEditingValue({...editingValue, Value: e.target.value})}
                                                                                onKeyPress={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        saveValueEdit(editingValue.Value);
                                                                                    } else if (e.key === 'Escape') {
                                                                                        cancelValueEdit();
                                                                                    }
                                                                                }}
                                                                                autoFocus
                                                                            />
                                                                            <button
                                                                                className="btn btn-sm btn-success"
                                                                                onClick={() => saveValueEdit(editingValue.Value)}
                                                                            >
                                                                                <i className="fa fa-check"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-secondary"
                                                                                onClick={() => cancelValueEdit()}
                                                                            >
                                                                                <i className="fa fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={value.Value}>
                                                                            {value.Value}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {editingValue?.Id !== value.Id && (
                                                                        <div>
                                                                            <i 
                                                                                style={{ cursor: 'pointer', color: '#17a2b8' }} 
                                                                                data-bs-tooltip="tooltip" 
                                                                                data-bs-placement='top' 
                                                                                title={t('edit', { ns: 'labels' })} 
                                                                                onClick={() => startEditingValue(value)}
                                                                                className="fa-regular fa-pen-to-square me-4 fa-2xl hover-icon-edit" 
                                                                            />
                                                                            <i 
                                                                                style={{ cursor: 'pointer', color: '#dc3545' }} 
                                                                                data-bs-tooltip="tooltip" 
                                                                                data-bs-placement='top' 
                                                                                title={t('delete', { ns: 'labels' })} 
                                                                                onClick={() => deleteValueLocally(value.Id)}
                                                                                className="fa fa-trash fa-2xl hover-icon-trash" 
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center text-muted">
                                            <i className="fa fa-arrow-left me-2"></i>
                                            {t('selectVocabularyToViewValues', { ns: 'labels' })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Form_Custom_Vocs_Screen; 